import * as THREE from 'three';

/**
 * Procedural models for boss-mechanic helpers that are effects rather than
 * creatures (`fx_*` ids in `MonsterDef.model`). Materials carry tint roles in
 * `userData.tint` so `ActorView` can colour each helper by its `MonsterDef.tint`:
 *
 * - `body`: `color` becomes the tint (lit surfaces);
 * - `glow`: `emissive` becomes the tint (self-lit, blooms);
 * - `light`: `color` of an unlit additive card becomes the tint.
 *
 * Objects named `fx_spin` rotate and `fx_bob` bob while the helper is alive.
 */

export interface FxModel {
  scene: THREE.Object3D;
  height: number;
  radius: number;
}

let glowTex: THREE.Texture | null = null;
function glowTexture(): THREE.Texture {
  if (glowTex) return glowTex;
  const s = 64;
  const c = document.createElement('canvas');
  c.width = c.height = s;
  const g = c.getContext('2d');
  if (!g) throw new Error('2D canvas unavailable');
  const grd = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
  grd.addColorStop(0, 'rgba(255,255,255,1)');
  grd.addColorStop(0.35, 'rgba(255,255,255,0.45)');
  grd.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grd;
  g.fillRect(0, 0, s, s);
  glowTex = new THREE.CanvasTexture(c);
  glowTex.colorSpace = THREE.SRGBColorSpace;
  return glowTex;
}

function body(params: THREE.MeshStandardMaterialParameters): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({ roughness: 0.45, metalness: 0.2, ...params });
  m.userData.tint = 'body';
  return m;
}

function glow(intensity: number, params: THREE.MeshStandardMaterialParameters = {}): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({ color: 0x202020, emissive: 0xffffff, emissiveIntensity: intensity, roughness: 0.3, ...params });
  m.userData.tint = 'glow';
  return m;
}

/** Additive ground card (fake light pool). */
function groundCard(size: number, opacity: number): THREE.Mesh {
  const mat = new THREE.MeshBasicMaterial({ map: glowTexture(), transparent: true, opacity, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false });
  mat.userData.tint = 'light';
  const m = new THREE.Mesh(new THREE.PlaneGeometry(size, size), mat);
  m.rotation.x = -Math.PI / 2;
  m.position.y = 0.04;
  m.renderOrder = 3;
  m.castShadow = false;
  m.receiveShadow = false;
  return m;
}

function ring(inner: number, outer: number, opacity: number): THREE.Mesh {
  const mat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, toneMapped: false });
  mat.userData.tint = 'light';
  const m = new THREE.Mesh(new THREE.RingGeometry(inner, outer, 64), mat);
  m.rotation.x = -Math.PI / 2;
  m.position.y = 0.05;
  m.renderOrder = 3;
  return m;
}

/** Floating energy orb: bright core, translucent shell, spinning halo, light pool. */
function orb(): FxModel {
  const g = new THREE.Group();
  const bob = new THREE.Group();
  bob.name = 'fx_bob';
  bob.position.y = 1.25;
  g.add(bob);
  const core = new THREE.Mesh(new THREE.IcosahedronGeometry(0.3, 2), glow(3.2, { color: 0xffffff }));
  const shell = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.5, 2),
    glow(0.9, { transparent: true, opacity: 0.45, depthWrite: false, roughness: 0.1, metalness: 0 }),
  );
  const spin = new THREE.Group();
  spin.name = 'fx_spin';
  const halo = new THREE.Mesh(new THREE.TorusGeometry(0.78, 0.035, 6, 48), glow(2.4));
  halo.rotation.x = Math.PI / 2.4;
  const halo2 = new THREE.Mesh(new THREE.TorusGeometry(0.66, 0.025, 6, 40), glow(2.0));
  halo2.rotation.set(Math.PI / 2, 0.9, 0);
  spin.add(halo, halo2);
  bob.add(core, shell, spin);
  g.add(groundCard(3.2, 0.55));
  return { scene: g, height: 1.8, radius: 0.6 };
}

/** Fused glass: a cluster of translucent shards and a faint ring marking the 2.6 m grounding field. */
function glassHusk(): FxModel {
  const g = new THREE.Group();
  const shardMat = body({ color: 0xffffff, roughness: 0.12, metalness: 0.15, transparent: true, opacity: 0.88, emissive: 0x000000 });
  const coreMat = glow(0.9, { transparent: true, opacity: 0.9 });
  const shards: [number, number, number, number, number][] = [
    [0, 0, 1.25, 0, 0],
    [0.32, 0.12, 0.9, 0.35, 0.5],
    [-0.3, 0.18, 0.8, -0.4, -0.3],
    [0.1, -0.34, 0.75, -0.25, 0.9],
    [-0.18, -0.2, 0.6, 0.45, -1.2],
    [0.42, -0.22, 0.5, 0.6, 2.1],
  ];
  for (const [x, z, h, tilt, yaw] of shards) {
    const geo = new THREE.OctahedronGeometry(0.28, 0);
    geo.scale(0.7, h / 0.56, 0.7);
    const m = new THREE.Mesh(geo, shardMat);
    m.position.set(x, h * 0.42, z);
    m.rotation.set(tilt * 0.5, yaw, tilt);
    m.castShadow = true;
    g.add(m);
  }
  const core = new THREE.Mesh(new THREE.IcosahedronGeometry(0.22, 1), coreMat);
  core.position.y = 0.35;
  g.add(core);
  g.add(ring(2.45, 2.6, 0.55), groundCard(5.4, 0.18), groundCard(1.8, 0.5));
  return { scene: g, height: 1.4, radius: 0.7 };
}

/** Prison cage: dark metal bars, glowing rune bands and a rune circle on the floor. */
function cage(): FxModel {
  const g = new THREE.Group();
  const barMat = body({ color: 0xffffff, metalness: 0.65, roughness: 0.38 });
  const runeMat = glow(2.6);
  const R = 0.95;
  const H = 2.5;
  const bars = 12;
  const barGeo = new THREE.CylinderGeometry(0.045, 0.06, H, 6);
  for (let i = 0; i < bars; i++) {
    const a = (i / bars) * Math.PI * 2;
    const b = new THREE.Mesh(barGeo, barMat);
    b.position.set(Math.sin(a) * R, H / 2, Math.cos(a) * R);
    b.castShadow = true;
    g.add(b);
  }
  for (const [y, r, t] of [
    [0.08, R + 0.05, 0.08],
    [H, R, 0.07],
  ] as const) {
    const tr = new THREE.Mesh(new THREE.TorusGeometry(r, t, 6, 36), barMat);
    tr.rotation.x = Math.PI / 2;
    tr.position.y = y;
    tr.castShadow = true;
    g.add(tr);
  }
  const spin = new THREE.Group();
  spin.name = 'fx_spin';
  for (const y of [0.9, 1.7]) {
    const band = new THREE.Mesh(new THREE.TorusGeometry(R + 0.06, 0.028, 4, 36), runeMat);
    band.rotation.x = Math.PI / 2;
    band.position.y = y;
    spin.add(band);
  }
  // Spire on top.
  const spire = new THREE.Mesh(new THREE.ConeGeometry(0.28, 0.9, 6), barMat);
  spire.position.y = H + 0.4;
  const gem = new THREE.Mesh(new THREE.OctahedronGeometry(0.16, 0), runeMat);
  gem.position.y = H + 0.95;
  g.add(spin, spire, gem, ring(R + 0.25, R + 0.4, 0.8), groundCard(3.4, 0.45));
  return { scene: g, height: H + 1.1, radius: R + 0.2 };
}

const BUILDERS: Record<string, () => FxModel> = {
  fx_orb: orb,
  fx_glass_husk: glassHusk,
  fx_cage: cage,
};

/** Builds the procedural model for an `fx_*` id (null for unknown ids). */
export function buildFxModel(id: string): FxModel | null {
  const b = BUILDERS[id];
  return b ? b() : null;
}
